import type { QuickFilterScopeStruct } from "@amzn/ads-blink-client";
import { QuickFilterScopeType, type QuickFilter } from "@amzn/ads-blink-client";
import {
    Button,
    Checkbox,
    Flex,
    HelpTip,
    Input,
    Modal,
    Text,
} from "@amzn/storm-ui";
import { trash } from "@amzn/storm-ui-icons";
import type { AsyncState } from "@react-hookz/web";
import type { ReactElement } from "react";
import { useEffect, useState } from "react";
import { useStore } from "zustand";

import { TELEMETRY_ID } from "../../../../constants/telemetry";
import {
    notificationStore,
    NotificationType,
} from "../../../../store/notification";
import { salesforceStore } from "../../../../store/salesforce";
import { unifiedFilterStore } from "../../../../store/unifiedFilters";
import type { PartialUnifiedfilterState } from "../../../../utils/filters.utils";
import { encodeUnifiedFilterStatePayload } from "../../../../utils/filters.utils";
import {
    getAdvertiserId,
    isAccountAgnosticId,
} from "../../../../utils/permission.utils";
import { logWithTakt } from "../../../../utils/telemetry.utils";

import { CreateQuickFilterActionAsync } from "./useCreateQuickFilterAction";
import { DeleteQuickFilterActionAsync } from "./useDeleteQuickFilterAction";
import { UpdateQuickFilterActionAsync } from "./useUpdateQuickFilterAction";

interface QuickFilterManagementModalProps {
    isModalOpen: boolean;
    setIsModalOpen: (open: boolean) => void;
    handleSubmit: () => void;
    quickFilter?: QuickFilter;
}

export const QuickFilterManagementModal: React.FC<
    QuickFilterManagementModalProps
> = ({
    isModalOpen,
    setIsModalOpen,
    handleSubmit,
    quickFilter,
}): ReactElement => {
    const [upsertNotification] = useStore(notificationStore, (state) => [
        state.upsertNotification,
    ]);

    const action = quickFilter ? "Update" : "Save";

    const [saveState, saveAction] = CreateQuickFilterActionAsync();

    const [updateState, updateAction] = UpdateQuickFilterActionAsync();

    const [deleteState, deleteAction] = DeleteQuickFilterActionAsync();

    const [isLoading, setIsLoading] = useState(false);

    const [newName, setNameName] = useState("");
    const [isGlobal, setIsGlobal] = useState(false);

    const [isLastSearchParametersDifferent, applyFilters] = useStore(
        unifiedFilterStore,
        (state) => [
            state.isLastSearchParametersDifferent,
            state.setIsContentDirty,
        ],
    );

    const closeModal = () => {
        setIsModalOpen(false);
    };

    useEffect(() => {
        if (isModalOpen && quickFilter) {
            setNameName(quickFilter.name ?? "");
            setIsGlobal(quickFilter.scopeType === QuickFilterScopeType.GLOBAL);
        } else {
            setNameName("");
            setIsGlobal(false);
        }
    }, [isModalOpen]);

    const getNewScope = (): QuickFilterScopeStruct => {
        if (isGlobal) {
            return {
                scopeType: QuickFilterScopeType.GLOBAL,
                scopeValue: QuickFilterScopeType.GLOBAL,
            };
        } else {
            if (isAccountAgnosticId()) {
                return {
                    scopeType: QuickFilterScopeType.AGNOSTIC,
                    scopeValue: QuickFilterScopeType.AGNOSTIC,
                };
            } else {
                return {
                    scopeType: QuickFilterScopeType.SFID,
                    scopeValue: getAdvertiserId(),
                };
            }
        }
    };

    const saveQuickFilter = async () => {
        const newScope = getNewScope();

        const { searchQuery, filters } = unifiedFilterStore.getState();

        const payloadToEncode: PartialUnifiedfilterState = {
            filters,
            searchQuery,
        };

        const filter = encodeUnifiedFilterStatePayload(payloadToEncode, false);

        if (!filter) {
            upsertNotification(
                "Failed to parse current filter state when saving. Please try again, or contact support.",
                NotificationType.ERROR,
            );
            return;
        }

        setIsLoading(true);
        let state: AsyncState<any>;
        if (quickFilter) {
            state = updateState;
            await updateAction.execute(quickFilter!, {
                newScope,
                newFilter: filter,
                newName,
            });

            logWithTakt(TELEMETRY_ID.QUICK_FILTER.UPDATE, {
                id: quickFilter.identifier,
            });
        } else {
            state = saveState;
            const newFilter = await saveAction.execute({
                ...newScope,
                filter,
                name: newName,
            });

            logWithTakt(TELEMETRY_ID.QUICK_FILTER.CREATE, {
                id: newFilter?.data?.identifier,
            });
        }
        setIsLoading(false);

        // Error states are handled in the actions themselves
        if (state.status !== "error") {
            upsertNotification(
                `Succesfully ${action.toLocaleLowerCase()}d your Quick Filter: ${newName}`,
                NotificationType.SUCCESS,
            );
            handleSubmit();
            closeModal();

            // If the user saved this search without actually performing it, go ahead and perform it
            if (isLastSearchParametersDifferent()) {
                applyFilters(true);
            }
        }
    };

    const deleteQuickFilter = async () => {
        setIsLoading(true);
        await deleteAction.execute(quickFilter!);
        setIsLoading(false);

        logWithTakt(TELEMETRY_ID.QUICK_FILTER.DELETE, {
            id: quickFilter!.identifier,
        });

        // Error states are handled in the actions themselves
        if (deleteState.status !== "error") {
            upsertNotification(
                `Succesfully deleted your quickfilter: ${quickFilter!.name}`,
                NotificationType.SUCCESS,
            );
            handleSubmit();
            closeModal();
        }
    };

    const Footer = () => (
        <>
            {quickFilter && (
                <Button
                    data-testid="quickfilter-modal-delete-button"
                    iconStart={trash}
                    onClick={() => void deleteQuickFilter()}
                    loading={isLoading}
                >
                    Delete
                </Button>
            )}
            <Button
                data-testid="quickfilter-modal-submit-button"
                onClick={() => void saveQuickFilter()}
                variant="primary"
                loading={isLoading}
            >
                {action}
            </Button>
        </>
    );

    return (
        <Modal
            isOpen={isModalOpen}
            header={action + " Quick Filter"}
            footer={<Footer />}
            onClose={closeModal}
        >
            <Flex direction="column" rowGap="xsmall">
                <Input
                    id={"quick-filter-name"}
                    fullWidth={true}
                    data-testid="quick-filter-name"
                    onChange={(event) => setNameName(event.target.value)}
                    placeholder="My Quick Filter"
                    label={"Quick Filter Name"}
                    value={newName}
                />

                <Checkbox
                    onChange={(event) => setIsGlobal(event.target.checked)}
                    checked={isGlobal}
                    data-testid="quick-filter-scope"
                    label={
                        <Text>
                            Save across all my SalesForce accounts
                            <HelpTip>
                                Leaving this unchecked will save this quick
                                filter only to the current SalesForce account:{" "}
                                <strong>
                                    {salesforceStore.getState().sfContext
                                        .name ?? "Account Agnostic"}
                                </strong>
                            </HelpTip>
                        </Text>
                    }
                />
            </Flex>
        </Modal>
    );
};
